import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  ComposedChart, Line, Area,
} from "recharts";
import { TrendingUp, Soup, Wallet, Coins, UserRound, Truck } from "lucide-react";
import api, { TRY, NUM } from "@/lib/api";
import { Kpi, Card } from "@/components/Cards";
import OverdueBanner from "@/components/OverdueBanner";
import MealBreakdown from "@/components/MealBreakdown";
import MissingEntriesBanner from "@/components/MissingEntriesBanner";
import CostAlertBanner from "@/components/CostAlertBanner";
import CardDueBanner from "@/components/CardDueBanner";
import ChequeDueBanner from "@/components/ChequeDueBanner";
import MonthlyReportCard from "@/components/MonthlyReportCard";
import DailyExpenses from "@/components/DailyExpenses";
import ShipmentSummaryCard from "@/components/ShipmentSummaryCard";

const PALETTE = ["#E27D38", "#38BDF8", "#10B981", "#F59E0B", "#A855F7", "#F43F5E"];

const ChartTip = ({ active, payload, label, currency = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold">{currency ? TRY(p.value) : NUM(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard({ goTo, isAdmin = true }) {
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState({ months: [], customers: [] });
  const [trend, setTrend] = useState([]);
  const [overdue, setOverdue] = useState(null);
  const [meals, setMeals] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const [d, m, t, o, mb] = await Promise.allSettled([
        api.get("/dashboard"),
        api.get("/reports/monthly-sales"),
        api.get("/reports/daily-trend"),
        api.get("/reminders/overdue"),
        api.get("/reports/meal-breakdown"),
      ]);
      if (d.status !== "fulfilled") { setError(d.reason?.response?.data?.detail || d.reason?.message || "Panel verisi alınamadı"); return; }
      setStats(d.value.data);
      if (m.status === "fulfilled") setMonthly(m.value.data);
      if (o.status === "fulfilled") setOverdue(o.value.data);
      if (mb.status === "fulfilled") setMeals(mb.value.data);
      if (t.status === "fulfilled") setTrend(t.value.data.map((x) => ({ ...x, gun: x.date.slice(8) + "." + x.date.slice(5, 7) })));
    })();
  }, []);

  if (error) return <div className="h-64 flex flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="dashboard-error">
    <span>Panel yüklenemedi: {String(error)}</span>
    <button onClick={() => window.location.reload()} className="text-primary underline text-sm" data-testid="dashboard-retry">Tekrar dene</button>
  </div>;
  if (!stats) return <div className="h-64 flex items-center justify-center text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Genel Bakış & Ciro</h1>
        <p className="text-sm text-muted-foreground mt-1">Bugünkü operasyon ve aylık finansal durum özeti.</p>
      </div>

      <OverdueBanner data={overdue} goTo={goTo} />
      <MissingEntriesBanner goTo={goTo} />
      {isAdmin && <CostAlertBanner goTo={goTo} />}
      {isAdmin && <CardDueBanner goTo={goTo} />}
      {isAdmin && <ChequeDueBanner goTo={goTo} />}
      {isAdmin && <MonthlyReportCard />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
        <Kpi index={0} testId="kpi-daily-revenue" label="Bugünkü Satış Cirosu" value={TRY(stats.daily_revenue)}
          sub={`${stats.active_firms} firma aktif`} icon={TrendingUp} color="primary" />
        <Kpi index={1} testId="kpi-total-headcount" label="Bugünkü Toplam Porsiyon" value={`${NUM(stats.daily_headcount)} kişi`}
          sub="Günlük yemek adedi" icon={Soup} color="sky" />
        <Kpi index={2} testId="kpi-receivable" label="Toplam Cari Alacak" value={TRY(stats.receivable_balance)}
          sub={`Vadesi geçen: ${TRY(stats.overdue)}`} icon={Wallet} color="amber" />
        {isAdmin && <Kpi index={3} testId="kpi-net-profit" label="Aylık Net Kâr" value={TRY(stats.net_profit)}
          sub={`%${(stats.margin ?? 0).toFixed(1)} net marj`} icon={Coins} color="emerald" />}
        <Kpi index={4} testId="kpi-staff-payable" label="Personele Borç" value={TRY(stats.staff_payable)}
          sub="Ödenmemiş hakediş" icon={UserRound} color="rose" />
        <Kpi index={5} testId="kpi-supplier-payable" label="Tedarikçilere Borç" value={TRY(stats.supplier_payable)}
          sub="Ödenmemiş alış faturası" icon={Truck} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-8 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Müşteri Bazında Aylık Ciro</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Son 6 ay, firma bazlı satış dağılımı</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthly.months} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => (v >= 1000 ? `${v / 1000}b` : v)} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--accent))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {monthly.customers.map((c, i) => (
                <Bar key={c} dataKey={c} stackId="a" fill={PALETTE[i % PALETTE.length]} radius={i === monthly.customers.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="lg:col-span-4 p-5" data-testid="profit-card">
          <h3 className="font-heading text-lg font-semibold mb-1">{isAdmin ? "Kâr / Zarar Özeti" : "Aylık Satış Özeti"}</h3>
          <p className="text-xs text-muted-foreground mb-4">Bu ay ({stats.date.slice(0, 7)})</p>
          <div className="space-y-3">
            <Row label="Aylık Ciro (KDV dahil)" value={TRY(stats.month_revenue)} accent="text-emerald-400" />
            {isAdmin ? (<>
            {stats.month_vat > 0 && <Row label="Aylık KDV" value={TRY(stats.month_vat)} accent="text-amber-400" />}
            <Row label="Toplam Maliyet" value={TRY(stats.month_costs)} accent="text-rose-400" />
            <div className="border-t border-border pt-3">
              <Row label="Net Kâr" value={TRY(stats.net_profit)} accent="text-primary" big />
            </div>
            <div className="mt-4 rounded-xl bg-muted/50 p-4 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Net Kâr Marjı</span>
                <span className="font-mono font-semibold">%{stats.margin.toFixed(1)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Porsiyon Başı Ort. Maliyet</span>
                <span className="font-mono font-semibold">{TRY(stats.avg_cost_per_portion)}</span>
              </div>
            </div>
            </>) : (<>
            <Row label="Toplam Cari Alacak" value={TRY(stats.receivable_balance)} accent="text-amber-400" />
            <Row label="Vadesi Geçen" value={TRY(stats.overdue)} accent="text-rose-400" />
            </>)}
          </div>
        </Card>
      </div>

      <DailyExpenses goTo={goTo} />

      <ShipmentSummaryCard goTo={goTo} />

      <MealBreakdown data={meals} />

      <Card className="p-5">
        <h3 className="font-heading text-lg font-semibold mb-1">Son 30 Günlük Ciro & Porsiyon Eğilimi</h3>
        <p className="text-xs text-muted-foreground mb-4">Günlük satış cirosu ve toplam porsiyon adedi</p>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={trend} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E27D38" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#E27D38" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="gun" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={3} />
            <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}b` : v)} />
            <Tooltip content={<ChartTip />} />
            <Area yAxisId="l" type="monotone" dataKey="revenue" name="Ciro" stroke="#E27D38" strokeWidth={2} fill="url(#rev)" />
            <Line yAxisId="l" type="monotone" dataKey="revenue" name="Ciro" stroke="#E27D38" strokeWidth={0} dot={false} legendType="none" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

const Row = ({ label, value, accent, big }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono font-bold ${big ? "text-xl" : "text-base"} ${accent}`}>{value}</span>
  </div>
);
